import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { Box } from '@mui/material';

import useRouter from '../../utils/useRouter';
import useScreenSize from '../../utils/useScreenSize';
import { CollectionProperties } from '../../types/AppContent';
import { routes } from '../../constants';
import { MenuItemProps } from '../cards/MuiCard/CustomMuiCard/CustomMuiCardActions/CustomMuiCardActions';
import { boxShadows, palette, spaces, typography } from '../../assets/styles/themes/tokens';
import CustomCollectionCard from '../cards/MuiCard/CustomMuiCard/CustomMuiCard';
import CollectionContent from '../MyCollections/MyCollectionsList/CollectionContent/CollectionContent';
import SubjectBanners from '../../assets/subjects/banners';
import { unfollowCollection } from '../../actions/collections.actions';
import { NotificationContext } from '../../state/NotificationContextProvider';

interface FollowedCollectionsListProps {
    collections: CollectionProperties[];
}

const FollowedCollectionsList = ({ collections }: FollowedCollectionsListProps) => {
  const { t } = useTranslation();
  const { router } = useRouter();
  const dispatch = useDispatch();
  const { isXsScreen, isSmScreen, isMdScreen } = useScreenSize();
  const { showNotification } = useContext(NotificationContext);

  const getGridTemplateColumns = () => {
    if (isXsScreen) return 1;
    if (isSmScreen || isMdScreen) return 2;
    return 3;
  };

  const openCollection = (collectionId: string) =>
    router.push(`/${routes.followed.root}/${collectionId}`);

  const onClickUnfollow = (collection: CollectionProperties) => {
    dispatch(unfollowCollection(collection));
    showNotification({
      message: t('You have successfully unsubscribed from the collection {{name}}.', { name: collection.title }),
      severity: 'success',
    });
  };

  const getMenuItems = (collection: CollectionProperties): MenuItemProps[] => [
    {
      label: t('No longer following'),
      iconName: 'minusCircle',
      onClick: () => onClickUnfollow(collection),
      color: palette.error.main,
    },
  ];

  const getBanner = (collection: CollectionProperties) => {
    if (!collection.isPublic) return undefined;

    const subject = collection.subjects?.[0];
    const SubjectBanner = SubjectBanners[subject] ?? SubjectBanners.Didaktik;

    return <SubjectBanner.small className='subject-banner' />;
  };

  return (
    <Box
      data-testid='followed-collections-list'
      sx={{
        display: 'grid',
        rowGap: spaces.md,
        columnGap: spaces.md,
        flexShrink: 0,
        alignSelf: 'stretch',
        gridTemplateColumns: `repeat(${getGridTemplateColumns()}, minmax(0, 1fr))`,
        gridAutoFlow: 'row',
      }}
    >
      {collections.map((collection) => (
        <CustomCollectionCard
          key={collection.id}
          title={collection.title}
          subheader={`${collection.contentsCount} ${collection.contentsCount === 1 ? t('Content') : t('Contents')}`}
          onClick={() => openCollection(collection.id)}
          menuItems={getMenuItems(collection)}
          color={collection.color}
          banner={getBanner(collection)}
          sxCardStyle={{
            main: {
              cursor: 'pointer',
              '&:focus-visible': {
                outline: 'none',
                borderColor: palette.primary.focusVisible,
                boxShadow: boxShadows.elevation2,
                '& .MuiCardHeader-title': {
                  color: palette.primary.main,
                },
              },
              '&:hover': {
                boxShadow: boxShadows.elevation2,
                '& .MuiCardHeader-title': {
                  color: palette.primary.main,
                },
              },
            },
            banner: {
              height: '180px',
              width: 'auto',
              maxWidth: '100%',
              '& .subject-banner': { height: '100%', width: '100%' },
            },
            subheader: {
              ...(typography.body2),
            },
          }}
        >
          <CollectionContent collection={collection}/>
        </CustomCollectionCard>
      ))}
    </Box>
  );
};

export default FollowedCollectionsList;
